import { useState, type ReactElement, type SyntheticEvent } from 'react'

import mark from '@/assets/relaii-mark.svg'
import styles from './SpaceLogo.module.css'

function cx(...values: Array<string | false | null | undefined>): string {
  return values.filter((value): value is string => typeof value === 'string' && value !== '').join(' ')
}

export type SpaceLogoSize = 'sm' | 'md' | 'lg' | 'xl'

export interface SpaceLogoProps {
  /** The Space's uploaded logo. Absent on a Space that has not set one. */
  src?: string | null
  /** The Space's name, which supplies the label unless the logo is decorative. */
  name: string
  /** 20, 28, 40 or 64 px. */
  size?: SpaceLogoSize
  /** Set when the Space name is already written next to the logo. */
  decorative?: boolean
  className?: string
}

/**
 * The face of a Space: its own square logo, or the relaii mark.
 *
 * A Space with no logo is the ordinary case rather than a failure, so the
 * fallback is the product mark as a real image with a real name — never an
 * empty box, and never a shape drawn in CSS. The mark is the vendored copy of
 * `web/assets/relaii-mark.svg`; it keeps its pale Mist plate in dark mode too,
 * which is what `design.md` asks for, so nothing here recolours or inverts it.
 *
 * An uploaded logo is cropped to a square by the server, but a client can be
 * looking at a logo pasted in as a URL by the onboarding wizard, which nobody
 * cropped. `object-fit: cover` makes both cases square here as well.
 */
export function SpaceLogo({
  src,
  name,
  size = 'md',
  decorative = false,
  className,
}: SpaceLogoProps): ReactElement {
  const logoSrc = typeof src === 'string' && src !== '' ? src : null

  // Which URL failed, rather than whether one did. A boolean would have to be
  // reset in an effect, which runs after the commit, so a new logo would render
  // the fallback for one frame before the effect cleared the flag.
  const [failedSrc, setFailedSrc] = useState<string | null>(null)

  const showLogo = logoSrc !== null && logoSrc !== failedSrc

  // Dropping the attribute stops the browser from painting its own broken-image
  // glyph in the moment between the error and the re-render.
  const onLogoError = (event: SyntheticEvent<HTMLImageElement>): void => {
    event.currentTarget.removeAttribute('src')
    setFailedSrc(logoSrc)
  }

  return (
    <span className={cx(styles.logo, styles[size], className)}>
      {showLogo ? (
        <img
          className={styles.image}
          src={logoSrc}
          alt={decorative ? '' : `${name} logo`}
          aria-hidden={decorative ? true : undefined}
          loading="lazy"
          decoding="async"
          onError={onLogoError}
        />
      ) : (
        <img
          className={styles.image}
          src={mark}
          alt={decorative ? '' : 'relaii'}
          aria-hidden={decorative ? true : undefined}
          decoding="async"
        />
      )}
    </span>
  )
}
